import {
  channelRow,
  findDeclaration,
  type EpisodeInspect,
  type InspectChannel,
} from "../../domain/episodeInspect";
import { streamCursorAt } from "../../feature/streamTimeFacts";
import styles from "./ChannelTable.module.css";

type ChannelTableProps = {
  inspect: EpisodeInspect;
  /** 배열까지 받은 채널들. 채널마다 시각이 따로 가므로 행도 채널마다 다르다. */
  channels: readonly InspectChannel[];
  /** 재생 클럭의 현재 시각(episode 공통 원점 기준 초). */
  currentSeconds: number;
};

// 한 셀에 펼치는 차원 수 상한 — 그 이상은 "외 N" 으로 접는다(긴 관절 벡터).
const MAX_DIMS_SHOWN = 12;

/** 값 표기 — 정수는 그대로, 실수는 소수 4자리. 원본을 반올림해 저장하지 않고 표시만 줄인다. */
export function formatChannelValue(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(4);
}

/**
 * 채널 표 — 채널마다 이름 · dtype · 차원 · **그 채널의** 현재 행과 값.
 *
 * 행은 채널마다 따로 찾는다 — 전역 행 하나를 모든 채널에 적용하면 rate 가 다른 채널의 값이
 * 엉뚱한 시각의 것이 된다. Δt 는 재생 시각과 그 채널이 실제로 본 표본 시각의 차이다. 값은 차원 이름이 있으면 `name=값` 으로 읽힌다. 20Hz 로 바뀌는
 * 셀이라 c6 `DataTable`(소스 · 행 모델) 대신 가벼운 표를 쓴다.
 */
export function ChannelTable({ channels, currentSeconds, inspect }: ChannelTableProps) {
  const vectors = channels.filter((channel) => channel.values !== null);
  if (vectors.length === 0) {
    return <p className={styles.empty}>이 episode 에는 vector 채널이 없습니다.</p>;
  }

  return (
    <div className={styles.frame}>
      <table className={styles.table} aria-label="채널 값">
        <thead>
          <tr>
            <th scope="col">채널</th>
            <th scope="col">dtype</th>
            <th scope="col" className={styles.numeric}>
              차원
            </th>
            <th scope="col" className={styles.numeric}>
              행
            </th>
            <th scope="col" className={styles.numeric}>
              표본 시각
            </th>
            <th scope="col" className={styles.numeric}>
              Δt
            </th>
            <th scope="col">현재 값</th>
          </tr>
        </thead>
        <tbody>
          {vectors.map((channel) => {
            const declaration = findDeclaration(inspect, channel.key);
            const cursor = streamCursorAt(channel, currentSeconds);
            const values = channelRow(channel, cursor.index);
            const shown = values.slice(0, MAX_DIMS_SHOWN);
            const hidden = values.length - shown.length;
            return (
              <tr key={channel.key} data-channel={channel.key}>
                <th scope="row" className={styles.key}>
                  {channel.key}
                </th>
                <td>{declaration?.dtype ?? "—"}</td>
                <td className={styles.numeric}>{declaration?.dim ?? values.length}</td>
                <td className={styles.numeric}>
                  {/* 원본 파일 행 번호가 있으면 그것, 없으면 미확인이다 — index 로 대체하지 않는다. */}
                  {cursor.row ?? "—"}
                </td>
                <td className={styles.numeric}>
                  {cursor.sampleTimeSeconds === null
                    ? "—"
                    : `${cursor.sampleTimeSeconds.toFixed(3)}s`}
                </td>
                <td className={styles.numeric} data-invalid={cursor.valid ? undefined : "true"}>
                  {/* 재생 시각과 그 채널이 실제로 본 표본 시각의 차이다. */}
                  {cursor.deltaSeconds === null
                    ? "—"
                    : `${(cursor.deltaSeconds * 1000).toFixed(0)}ms`}
                  {cursor.valid ? null : " · 무효"}
                </td>
                <td className={styles.values}>
                  {shown.map((value, index) => (
                    <span key={index} className={styles.value}>
                      {declaration?.names ? (
                        <span className={styles.dimName}>
                          {declaration.names[index] ?? `#${index}`}
                        </span>
                      ) : null}
                      {formatChannelValue(value)}
                    </span>
                  ))}
                  {hidden > 0 ? <span className={styles.more}>외 {hidden}</span> : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
